import type { YachtCategory, YachtDieValue, YachtScoringDice } from "../types";
import { getUpperFaceValue, isUpperCategory } from "./categories";

function sumDice(dice: YachtScoringDice): number {
  return dice.reduce((total, die) => total + die, 0);
}

function countDice(dice: YachtScoringDice): ReadonlyMap<YachtDieValue, number> {
  const counts = new Map<YachtDieValue, number>();
  for (const die of dice) {
    counts.set(die, (counts.get(die) ?? 0) + 1);
  }
  return counts;
}

function hasExactFaces(
  dice: YachtScoringDice,
  expected: readonly YachtDieValue[],
): boolean {
  const sortedDice = [...dice].sort((a, b) => a - b);
  return expected.every((value, index) => sortedDice[index] === value);
}

export function scoreCategory(
  category: YachtCategory,
  dice: YachtScoringDice,
): number {
  if (isUpperCategory(category)) {
    const face = getUpperFaceValue(category);
    return dice
      .filter((die) => die === face)
      .reduce((total) => total + face, 0);
  }

  const sum = sumDice(dice);
  const counts = [...countDice(dice).values()].sort((a, b) => a - b);

  switch (category) {
    case "CHOICE":
      return sum;
    case "FOUR_OF_A_KIND":
      return counts.some((count) => count >= 4) ? sum : 0;
    case "FULL_HOUSE":
      return counts.length === 2 && counts[0] === 2 && counts[1] === 3
        ? sum
        : 0;
    case "SMALL_STRAIGHT":
      return hasExactFaces(dice, [1, 2, 3, 4, 5]) ? 15 : 0;
    case "LARGE_STRAIGHT":
      return hasExactFaces(dice, [2, 3, 4, 5, 6]) ? 30 : 0;
    case "YACHT":
      return counts.length === 1 && counts[0] === 5 ? 50 : 0;
    default:
      return 0;
  }
}
